import { useMemo } from 'react';
import { cn } from '@/lib/classNames';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';

export type PaginationProps = {
	currentPage: number;
	totalPages: number;
	onNextPage: () => void;
	onPreviousPage: () => void;
	onGotoPage: (page: number) => void;
	isLoading?: boolean;
};

export const Pagination = ({
	currentPage,
	totalPages,
	onGotoPage,
	onNextPage,
	onPreviousPage,
	isLoading = false
}: PaginationProps) => {
	const allPages = useMemo(() => {
		const pagination = new Set<string | number>([1]);

		// Always include the first page
		pagination.add(1);

		// Add '...' if there is a gap before the current page range
		const startRange = Math.max(2, currentPage - 2);
		if (startRange > 2) {
			pagination.add('...');
		}

		// Add pages around the current page
		for (
			let i = startRange;
			i <= Math.min(totalPages - 1, currentPage + 2);
			i++
		) {
			pagination.add(i);
		}

		// Add '...' if there is a gap after the current page range
		const endRange = Math.min(totalPages - 1, currentPage + 2);
		if (endRange < totalPages - 1) {
			pagination.add('...');
		}

		// Always include the last page if it is greater than 1
		if (totalPages > 1) {
			pagination.add(totalPages);
		}

		return Array.from(pagination);
	}, [currentPage, totalPages]);

	const hasPrevious = currentPage > 1;
	const hasNext = currentPage < totalPages;

	const handlePrevPageButtonClick = () => {
		if (!hasPrevious || isLoading) return null;

		return onPreviousPage();
	};
	const handleNextPageButtonClick = () => {
		if (!hasNext || isLoading) return null;

		return onNextPage();
	};
	const handlePageNumberButtonClick = (page: number) => {
		if (isLoading) return null;

		return onGotoPage(page);
	};

	return (
		<nav className="flex items-center gap-4">
			<button
				type="button"
				onClick={handlePrevPageButtonClick}
				className="cursor-pointer disabled:cursor-not-allowed"
				disabled={!hasPrevious || isLoading}
			>
				<ChevronLeftRounded />
			</button>

			<section className="flex items-center gap-4">
				{allPages.map((page, index) => {
					if (typeof page === 'string') {
						return (
							<span key={`${page}_${allPages[index - 1]}`}>
								{page}
							</span>
						);
					}

					const isActive = currentPage === page;

					return (
						<button
							type="button"
							key={page}
							data-is-active={isActive}
							disabled={isLoading}
							className={cn(
								'inline-flex aspect-square size-10 cursor-pointer items-center justify-center rounded-full border border-transparent p-2 text-dm-gray transition-[colors,opacity]',
								'hover:border-orange hover:bg-orange hover:text-dm-black',
								'disabled:cursor-not-allowed disabled:text-dm-gray disabled:opacity-60',
								'data-[is-active=true]:border-orange data-[is-active=true]:text-orange data-[is-active=true]:hover:text-dm-black'
							)}
							onClick={() => handlePageNumberButtonClick(page)}
						>
							{page}
						</button>
					);
				})}
			</section>

			<button
				type="button"
				className="cursor-pointer disabled:cursor-not-allowed"
				onClick={handleNextPageButtonClick}
				disabled={!hasNext || isLoading}
			>
				<ChevronRightRounded />
			</button>
		</nav>
	);
};
